// Request/response schemas for /auth/* + /family/kids/* endpoints.
// docs/replan-v2.md.

import { z } from 'zod';

// ─── Primitives ─────────────────────────────────────────────────────────────

export const localeSchema = z.enum(['ar', 'en']);

// RFC 5321 caps at 320. Trim + lowercase to match the case-insensitive
// uniqueness in users.email.
export const emailSchema = z.string().trim().toLowerCase().min(3).max(320).pipe(z.email());

// Username: 3-40 chars, starts with a letter, lowercase + digit + . + _.
// Matches the DB CHECK constraint exactly so a server-side validation never
// surprises the client.
export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z][a-z0-9_.]{2,39}$/, 'Letters, digits, . and _ only — must start with a letter');

// Strong policy: 10+ chars, letter + digit. Tighter checks live in
// packages/auth/password-policy.checkParentPassword and run server-side
// for the final word.
export const parentPasswordSchema = z
  .string()
  .min(10, 'At least 10 characters')
  .max(256)
  .refine((s) => /[a-zA-Z]/.test(s) && /\d/.test(s), {
    message: 'Must include a letter and a digit',
  });

// Looser policy for kids/students (tablet-friendly). 6+ chars, must include
// at least one letter so the password isn't a pure 6-digit code.
export const kidPasswordSchema = z
  .string()
  .min(6, 'At least 6 characters')
  .max(64)
  .refine((s) => /[a-zA-Z]/.test(s), { message: 'Must include at least one letter' });

// Login takes ANY non-empty password; server verifies against whichever
// policy is appropriate at the moment of the user's last password change.
export const anyPasswordSchema = z.string().min(1).max(256);

// ─── Adult registration (parents — schools provision admins separately) ─────

export const registerInput = z.object({
  email: emailSchema,
  password: parentPasswordSchema,
  displayName: z.string().trim().min(1).max(120),
  locale: localeSchema.optional().default('ar'),
  country: z.string().length(2).toUpperCase().optional(),
  marketingOptIn: z.boolean().optional().default(false),
});
export type RegisterInput = z.infer<typeof registerInput>;

// ─── Login (email OR username) ──────────────────────────────────────────────

export const loginInput = z
  .object({
    email: emailSchema.optional(),
    username: usernameSchema.optional(),
    password: anyPasswordSchema,
  })
  .refine((v) => Boolean(v.email) !== Boolean(v.username), {
    message: 'Provide either email or username, not both',
  });
export type LoginInput = z.infer<typeof loginInput>;

// ─── Magic link ─────────────────────────────────────────────────────────────

export const magicLinkRequestInput = z.object({
  email: emailSchema,
});
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestInput>;

export const magicLinkConsumeQuery = z.object({
  token: z.string().min(20).max(200),
});
export type MagicLinkConsumeQuery = z.infer<typeof magicLinkConsumeQuery>;

// ─── Password reset (email-only — kids reset via parent) ────────────────────

export const passwordResetRequestInput = z.object({
  email: emailSchema,
});
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestInput>;

export const passwordResetConfirmInput = z.object({
  token: z.string().min(20).max(200),
  password: parentPasswordSchema,
});
export type PasswordResetConfirmInput = z.infer<typeof passwordResetConfirmInput>;

// ─── Role switch (multi-role users) ─────────────────────────────────────────

export const roleSwitchInput = z.object({
  // 'parent', 'teacher', 'school_admin:<schoolId>', 'support', etc.
  // Server validates against the user's actual role memberships.
  context: z.string().min(1).max(80),
});
export type RoleSwitchInput = z.infer<typeof roleSwitchInput>;

// ─── Kid provisioning (parent-only) ─────────────────────────────────────────

export const kidCreateInput = z.object({
  username: usernameSchema,
  password: kidPasswordSchema,
  displayName: z.string().trim().min(1).max(120),
  avatar: z.string().trim().min(1).max(60),
  dob: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  grade: z.enum(['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6']).nullable().optional(),
  localePref: localeSchema.optional().default('ar'),
  tashkeelMode: z.enum(['with', 'partial', 'without']).optional().default('with'),
});
export type KidCreateInput = z.infer<typeof kidCreateInput>;

export const kidUpdateInput = z.object({
  displayName: z.string().trim().min(1).max(120).optional(),
  avatar: z.string().trim().min(1).max(60).optional(),
  dob: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  grade: z.enum(['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6']).nullable().optional(),
  localePref: localeSchema.optional(),
  tashkeelMode: z.enum(['with', 'partial', 'without']).optional(),
});
export type KidUpdateInput = z.infer<typeof kidUpdateInput>;

export const kidResetPasswordInput = z.object({
  password: kidPasswordSchema,
});
export type KidResetPasswordInput = z.infer<typeof kidResetPasswordInput>;

// ─── Username availability ──────────────────────────────────────────────────

export const usernameAvailabilityQuery = z.object({
  candidate: usernameSchema,
});
export type UsernameAvailabilityQuery = z.infer<typeof usernameAvailabilityQuery>;

// ─── Response shapes ────────────────────────────────────────────────────────

export const authUserDto = z.object({
  id: z.uuid(),
  email: z.string().nullable(),
  username: z.string().nullable(),
  locale: localeSchema,
  emailVerifiedAt: z.string().nullable(),
  displayName: z.string().nullable(),
  /** Currently selected role context (null until /auth/role-switch is called). */
  currentRoleContext: z.string().nullable(),
  /** All role contexts the user is eligible for. */
  availableRoleContexts: z.array(z.string()),
});
export type AuthUserDto = z.infer<typeof authUserDto>;

export const kidDto = z.object({
  id: z.uuid(),
  username: z.string(),
  displayName: z.string().nullable(),
  avatar: z.string().nullable(),
  dob: z.string().nullable(),
  grade: z.string().nullable(),
  localePref: localeSchema,
  tashkeelMode: z.string(),
  createdAt: z.string(),
});
export type KidDto = z.infer<typeof kidDto>;

export const usernameAvailabilityDto = z.object({
  candidate: z.string(),
  available: z.boolean(),
  suggestions: z.array(z.string()),
});
export type UsernameAvailabilityDto = z.infer<typeof usernameAvailabilityDto>;

export const okResponse = z.object({ ok: z.literal(true) });

export const errorResponse = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponse>;
